import '@plone/volto/config';
import { DownloadsBlockView, DownloadsBlockEdit } from './components';
import sidebar from './reducers/sidebar';
import artifacts from './reducers/artifact';
import subtitleGeneration from './reducers/subtitleGeneration';
import mediaPageEnriched from './reducers/mediaPageEnriched';
import {
  UploadButtonView,
  UploadButtonEdit,
  VideoJSEdit,
} from 'volto-base-addon/components';

import CustomEditWrapper from 'volto-base-addon/components/manage/Edit/CustomEditWrapper';
import SubtitleView from 'volto-base-addon/components/theme/SubtitleView/SubtitleView';
import MediaPageView from 'volto-base-addon/components/theme/MediaPageView';
import SubtitleEdit from 'volto-base-addon/components/manage/Subtitle/SubtitleEdit';
import videoIcon from 'volto-base-addon/icons/videojs.svg';
import { EPICS_TASK_VIEWS } from 'volto-epics-addon/constants';
import {
  TASK_UPLOAD_PROMPT,
  TASK_UPLOAD_BASICINFO_PROMPT,
  TASK_UPLOAD_WAITFORJOBS_VIEW,
  TASK_INTERMEDIATE_JOBS_VIEW,
  TASK_UPLOAD_DETAILS_PROMPT,
  TASK_UPLOAD_TRIM_PROMPT,
  MEDIA_PAGE_WOWZA_ROUTE,
  MEDIA_PAGE_TRANSCRIPTION_ROUTE,
  MEDIA_PAGE_PREVIEW_ROUTE,
  MEDIA_PAGE_STORYBOARDS_ROUTE,
  UID_VARIABLES_ROUTE,
} from 'volto-base-addon/constants';

import UploadStartPage from 'volto-base-addon/components/theme/FileUpload/UploadStartPage';
import UploadPromptPage from 'volto-base-addon/components/theme/FileUpload/UploadPromptPage';
import UploadBasicInfoPromptPage from 'volto-base-addon/components/theme/FileUpload/UploadBasicInfoPromptPage';
import UploadWaitForJobsPage from 'volto-base-addon/components/theme/FileUpload/UploadWaitForJobsPage';
import WaitIntermediateJobsPage from 'volto-base-addon/components/theme/FileUpload/WaitIntermediateJobsPage';
import UploadDetailsPromptPage from 'volto-base-addon/components/theme/FileUpload/UploadDetailsPromptPage';
import UploadTrimPromptPage from 'volto-base-addon/components/theme/FileUpload/UploadTrimPromptPage';
import { MediaPageVideoJSView } from 'volto-base-addon/components';

import heroSVG from '@plone/volto/icons/hero.svg';

const applyConfig = (config) => {
  config.blocks.blocksConfig.mediapagevideojs = {
    id: 'mediapagevideojs',
    title: 'Media Page VideoJS',
    group: 'common',
    icon: videoIcon,
    view: MediaPageVideoJSView,
    edit: VideoJSEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
    blockHasOwnFocusManagement: true,
    security: {
      addPermission: [],
      view: [],
    },
  };

  config.blocks.blocksConfig.downloads = {
    id: 'downloads',
    title: 'Downloads',
    icon: heroSVG,
    group: 'media',
    view: DownloadsBlockView,
    edit: DownloadsBlockEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  config.blocks.blocksConfig.uploadbutton = {
    id: 'uploadbutton',
    title: 'Upload Button',
    icon: heroSVG,
    group: 'common',
    view: UploadButtonView,
    edit: UploadButtonEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  config.addonRoutes.push(
    ...[
      {
        path: '/file-upload',
        component: UploadStartPage,
      },
      {
        path: EPICS_TASK_VIEWS[TASK_UPLOAD_PROMPT],
        component: UploadPromptPage,
      },
      {
        path: EPICS_TASK_VIEWS[TASK_UPLOAD_BASICINFO_PROMPT],
        component: UploadBasicInfoPromptPage,
      },
      {
        path: EPICS_TASK_VIEWS[TASK_UPLOAD_WAITFORJOBS_VIEW],
        component: UploadWaitForJobsPage,
      },
      {
        path: EPICS_TASK_VIEWS[TASK_INTERMEDIATE_JOBS_VIEW],
        component: WaitIntermediateJobsPage,
      },
      {
        path: EPICS_TASK_VIEWS[TASK_UPLOAD_DETAILS_PROMPT],
        component: UploadDetailsPromptPage,
      },
      {
        path: EPICS_TASK_VIEWS[TASK_UPLOAD_TRIM_PROMPT],
        component: UploadTrimPromptPage,
      },
      // Custom editor dispatch: intercepts the /edit route and checks
      // if a custom editor is registered for the content type. Falls through
      // to Volto's stock Edit component if not.
      {
        path: ['/edit', '/**/edit'],
        component: CustomEditWrapper,
      },
    ],
  );

  config.addonReducers = {
    ...config.addonReducers,
    sidebar,
    artifacts,
    subtitleGeneration,
    mediaPageEnriched,
  };

  // Types hidden from the add-menu even when allowed by FTI
  // (these are created programmatically by middleware/Camunda, not by users)
  config.settings.hiddenFromAddMenu = ['media_page', 'transcoding'];

  config.settings.nonContentRoutes = [
    ...config.settings.nonContentRoutes,
    '/file-upload',
    EPICS_TASK_VIEWS[TASK_UPLOAD_PROMPT],
    EPICS_TASK_VIEWS[TASK_UPLOAD_BASICINFO_PROMPT],
    EPICS_TASK_VIEWS[TASK_UPLOAD_WAITFORJOBS_VIEW],
    EPICS_TASK_VIEWS[TASK_INTERMEDIATE_JOBS_VIEW],
    EPICS_TASK_VIEWS[TASK_UPLOAD_DETAILS_PROMPT],
    EPICS_TASK_VIEWS[TASK_UPLOAD_TRIM_PROMPT],
    new RegExp(`${config.settings.publicPath}/${MEDIA_PAGE_WOWZA_ROUTE}/.*`),
    new RegExp(
      `${config.settings.publicPath}/${MEDIA_PAGE_TRANSCRIPTION_ROUTE}/.*`,
    ),
    new RegExp(`${config.settings.publicPath}/${MEDIA_PAGE_PREVIEW_ROUTE}/.*`),
    new RegExp(
      `${config.settings.publicPath}/${MEDIA_PAGE_STORYBOARDS_ROUTE}/.*`,
    ),
    new RegExp(`${config.settings.publicPath}/${UID_VARIABLES_ROUTE}/.*`),
  ];

  // Custom editors: content types that get a custom edit view instead of
  // the standard Volto block editor. The CustomEditWrapper dispatches here.
  config.settings.customEditors = {
    ...(config.settings.customEditors || {}),
    subtitle: SubtitleEdit,
  };

  // Content type views: custom view components for specific content types
  config.views.contentTypesViews = {
    ...config.views.contentTypesViews,
    subtitle: SubtitleView,
    media_page: MediaPageView,
  };

  return config;
};

export default applyConfig;
